/**
 * React hooks that bind the Coral-Placer workspace + coral-edit overlay to
 * the Pi via `useServerBackedState` (`/api/userdata/:namespace`). Values
 * are normalized on read so a malformed or older blob can never crash the
 * UI, and every write is persisted to the Pi (with a localStorage mirror
 * fallback for offline/deploy windows).
 */
import {
  useServerBackedState,
  type ServerBackedSource,
} from '../hooks/useServerBackedState';

import type { CoralEdits, Workspace } from './types';
import { DEFAULT_WORKSPACE, normalizeEdits, normalizeWorkspace } from './workspace';
import { normalizeSavedLayouts, type SavedLayout } from './savedLayouts';

/** Pi userdata namespace holding the full placement workspace. */
export const WORKSPACE_NAMESPACE = 'coral-placer-workspace';
/** Pi userdata namespace holding the coral-catalog edit overlay. */
export const EDITS_NAMESPACE = 'coral-placer-edits';
/** Pi userdata namespace holding named, reloadable placement layouts. */
export const LAYOUTS_NAMESPACE = 'coral-placer-layouts';

export interface WorkspaceController {
  readonly workspace: Workspace;
  readonly setWorkspace: (next: Workspace) => void;
  readonly loading: boolean;
  readonly source: ServerBackedSource;
  readonly error: string | null;
}

export function useWorkspace(): WorkspaceController {
  const state = useServerBackedState<Workspace>(
    WORKSPACE_NAMESPACE,
    DEFAULT_WORKSPACE,
  );
  return {
    workspace: normalizeWorkspace(state.value),
    setWorkspace: state.setValue,
    loading: state.loading,
    source: state.source,
    error: state.error,
  };
}

export interface EditsController {
  readonly edits: CoralEdits;
  readonly setEdits: (next: CoralEdits) => void;
  readonly source: ServerBackedSource;
}

export function useCoralEdits(): EditsController {
  const state = useServerBackedState<CoralEdits>(EDITS_NAMESPACE, {});
  return {
    edits: normalizeEdits(state.value),
    setEdits: state.setValue,
    source: state.source,
  };
}

export interface SavedLayoutsController {
  readonly layouts: SavedLayout[];
  readonly setLayouts: (next: SavedLayout[]) => void;
  readonly source: ServerBackedSource;
}

export function useSavedLayouts(): SavedLayoutsController {
  const state = useServerBackedState<SavedLayout[]>(LAYOUTS_NAMESPACE, []);
  return {
    layouts: normalizeSavedLayouts(state.value),
    setLayouts: state.setValue,
    source: state.source,
  };
}
